import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/axios';
import { MEDIA_CATEGORY_ORDER, catTR, DAY_TR } from './gbpLabels';
import {
  X, Star, Loader2, Globe, MapPin, Phone, Clock, Tag, Hash, AlertTriangle,
  MessageSquare, Navigation, MousePointerClick, Eye, ExternalLink, Store,
  Reply, Trash2, Send, Pencil, Image as ImageIcon, ImageOff, Play,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

/**
 * GbpLocationModal — tek lokasyonun detayı: künye + puan + son yorumlar +
 * son 30 günün Business Profile performansı.
 *
 * Künye alanları zaten listeden gelir (aynı readMask), o yüzden pencere anında
 * açılır; yorum ve performans `/gbp-brand/location/:id` ile sonradan yüklenir.
 *
 * 🔴 Yorumlar yalnızca İLK SAYFA (en yeni 20). "Yanıtsız" sayısı da bu sayfaya
 * aittir — tüm geçmişin yanıtsız toplamı DEĞİL, arayüzde öyle etiketlenir.
 *
 * Fotoğraflar iki ayrı kaynaktan gelir: işletmenin yüklediği kareler (kapak,
 * logo, dış/iç mekân…) ve müşterilerin yüklediği kareler. Müşteri fotoğrafları
 * işletme tarafından yönetilemez — bu yüzden ayrı bölümde, açıkça etiketli.
 */


const nf = new Intl.NumberFormat('tr-TR');
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

function Stars({ value, size = 'w-4 h-4' }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} yıldız`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`}
        />
      ))}
    </span>
  );
}

function Field({ icon: Icon, label, children }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-2.5 py-2">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
        <div className="text-sm text-gray-800 break-words">{children}</div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-500">
        <Icon className="w-3.5 h-3.5 text-gray-400" /> {label}
      </div>
      <div className="text-2xl font-semibold text-gray-900 mt-1.5 leading-none">{nf.format(value || 0)}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

/**
 * Günlük gösterim eğrisi. Tek seri olduğu için lejant yok — başlık seriyi
 * adlandırır. İmleç en yakın güne kilitlenir, ipucu HTML katmanında durur.
 */
function ImpressionsChart({ series }) {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);

  const points = useMemo(() => series.map((d) => ({
    date: d.date,
    value: (d.BUSINESS_IMPRESSIONS_DESKTOP_MAPS || 0) + (d.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH || 0)
      + (d.BUSINESS_IMPRESSIONS_MOBILE_MAPS || 0) + (d.BUSINESS_IMPRESSIONS_MOBILE_SEARCH || 0),
  })), [series]);

  if (points.length < 2) return null;

  const W = 720, H = 140, PAD_T = 10, PAD_B = 20, PAD_X = 4;
  const max = Math.max(...points.map((p) => p.value), 1);
  const x = (i) => PAD_X + (i * (W - PAD_X * 2)) / (points.length - 1);
  const y = (v) => PAD_T + (1 - v / max) * (H - PAD_T - PAD_B);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${H - PAD_B} L${x(0).toFixed(1)},${H - PAD_B} Z`;

  const onMove = (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const rel = ((e.clientX - r.left) / r.width) * W;
    const i = Math.max(0, Math.min(points.length - 1, Math.round(((rel - PAD_X) / (W - PAD_X * 2)) * (points.length - 1))));
    setHover(i);
  };

  const hp = hover != null ? points[hover] : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wide text-gray-500">Günlük gösterim (Haritalar + Arama)</div>
        <div className="text-[11px] text-gray-400">en yüksek {nf.format(max)}</div>
      </div>
      <div ref={wrapRef} className="relative mt-2" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[140px]" preserveAspectRatio="none">
          <defs>
            <linearGradient id="gbpImpFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
            </linearGradient>
          </defs>
          <line x1="0" y1={H - PAD_B} x2={W} y2={H - PAD_B} stroke="#e5e7eb" strokeWidth="1" />
          <path d={area} fill="url(#gbpImpFill)" />
          <path d={line} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          {hp && (
            <>
              <line x1={x(hover)} y1={PAD_T} x2={x(hover)} y2={H - PAD_B} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
              <circle cx={x(hover)} cy={y(hp.value)} r="4" fill="#2563eb" stroke="#fff" strokeWidth="2" vectorEffect="non-scaling-stroke" />
            </>
          )}
        </svg>
        {hp && (
          <div
            className="pointer-events-none absolute -top-1 px-2 py-1 rounded-lg bg-gray-900 text-white text-[11px] whitespace-nowrap shadow"
            style={{ left: `${(x(hover) / W) * 100}%`, transform: 'translate(-50%, -100%)' }}
          >
            {new Date(hp.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} · {nf.format(hp.value)} gösterim
          </div>
        )}
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>{new Date(points[0].date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}</span>
          <span>{new Date(points.at(-1).date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}</span>
        </div>
      </div>
    </div>
  );
}

const REPLY_MAX = 4096;

/**
 * Tek yorum + yanıt kutusu. Yanıt Google'da ANINDA yayımlanır ve yorum başına
 * tek yanıt tutulur (yeni yanıt eskisini ezer), o yüzden gönder/sil eylemleri
 * açıkça etiketli ve silme çift tıkla onaylanıyor.
 */
function ReviewCard({ review: r, onSubmit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(r.reply || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const open = () => { setDraft(r.reply || ''); setErr(null); setEditing(true); };

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true); setErr(null);
    try {
      await onSubmit(r.id, text);
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Yanıt gönderilemedi');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true); setErr(null);
    try {
      await onDelete(r.id);
      setConfirmDelete(false);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Yanıt silinemedi');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-3">
      {r.photo ? (
        <img src={r.photo} alt="" className="w-8 h-8 rounded-full shrink-0 object-cover" referrerPolicy="no-referrer" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gray-100 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">{r.author}</span>
          <Stars value={r.stars} size="w-3.5 h-3.5" />
          <span className="text-[11px] text-gray-400">{fmtDate(r.createTime)}</span>
          {!r.reply && !editing && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">yanıtsız</span>
          )}
        </div>

        {r.comment ? (
          <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{r.comment}</p>
        ) : (
          <p className="text-sm text-gray-400 mt-1 italic">Yorum metni yok, yalnızca puan verilmiş.</p>
        )}
        {r.translated && (
          <details className="mt-1">
            <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-gray-600">Google çevirisi</summary>
            <p className="text-sm text-gray-500 mt-1 whitespace-pre-line">{r.translated}</p>
          </details>
        )}

        {/* Mevcut yanıt */}
        {r.reply && !editing && (
          <div className="mt-2 pl-3 border-l-2 border-blue-100">
            <div className="text-[11px] text-gray-400 flex items-center gap-2 flex-wrap">
              İşletme yanıtı · {fmtDate(r.replyTime)}
              <button onClick={open} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700">
                <Pencil className="w-3 h-3" /> Düzenle
              </button>
              {confirmDelete ? (
                <span className="inline-flex items-center gap-2">
                  <span className="text-rose-700">Yanıt Google'dan silinsin mi?</span>
                  <button onClick={remove} disabled={busy}
                          className="text-rose-600 hover:text-rose-700 font-medium disabled:opacity-50">
                    {busy ? 'Siliniyor…' : 'Evet, sil'}
                  </button>
                  <button onClick={() => setConfirmDelete(false)} className="text-gray-500 hover:text-gray-700">Vazgeç</button>
                </span>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-1 text-gray-400 hover:text-rose-600">
                  <Trash2 className="w-3 h-3" /> Sil
                </button>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-line">{r.reply}</p>
          </div>
        )}

        {/* Yanıtla düğmesi */}
        {!r.reply && !editing && (
          <button onClick={open}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium">
            <Reply className="w-3.5 h-3.5" /> Yanıtla
          </button>
        )}

        {/* Yanıt yazma kutusu */}
        {editing && (
          <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50/30 p-2.5">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, REPLY_MAX))}
              rows={3}
              autoFocus
              placeholder="Müşteriye yanıtınız… (Google'da işletme yanıtı olarak herkese görünür)"
              className="w-full text-sm rounded-lg border border-gray-200 p-2 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 resize-y"
            />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button
                onClick={send}
                disabled={busy || !draft.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {r.reply ? 'Yanıtı güncelle' : 'Yanıtı yayımla'}
              </button>
              <button onClick={() => { setEditing(false); setErr(null); }}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-600 hover:bg-gray-50">
                Vazgeç
              </button>
              <span className="text-[11px] text-gray-400 ml-auto">{draft.length}/{REPLY_MAX}</span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1.5">
              {r.reply
                ? 'Güncelleme mevcut yanıtın yerine geçer — Google eski metni saklamaz.'
                : 'Yanıt doğrudan Google Haritalar profilinde yayımlanır.'}
            </div>
            {err && (
              <div className="mt-2 text-[11px] text-rose-700 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {err}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Galeri karesi. Google küçük görseli `=s300` olarak verir; ızgara 160px
 * civarında olduğu için bu boyut yeterli, tam boy yalnızca büyütünce çekilir.
 */
function PhotoTile({ item, onOpen }) {
  const [broken, setBroken] = useState(false);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-100 aspect-square"
      title={`${catTR(item.category)}${item.createTime ? ' · ' + fmtDate(item.createTime) : ''}`}
    >
      {broken || !item.thumb ? (
        <span className="absolute inset-0 flex items-center justify-center text-gray-300">
          <ImageOff className="w-5 h-5" />
        </span>
      ) : (
        <img
          src={item.thumb}
          alt={catTR(item.category)}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          className="w-full h-full object-cover transition group-hover:scale-[1.03]"
        />
      )}
      {item.format === 'VIDEO' && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
          <Play className="w-6 h-6 text-white fill-white" />
        </span>
      )}
      {(item.category === 'COVER' || item.category === 'LOGO') && (
        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-gray-900/70 text-white text-[10px]">
          {catTR(item.category)}
        </span>
      )}
    </button>
  );
}

/**
 * Büyütme katmanı. Modal'ın kendi Escape'i pencereyi kapattığı için burada
 * ayrı bir tuş dinleyicisi YOK — sıra üst bileşende yönetilir (önce büyütme
 * kapanır, sonra pencere).
 */
function PhotoViewer({ items, index, onIndex, onClose }) {
  const item = items[index];
  if (!item) return null;
  const go = (d) => onIndex((index + d + items.length) % items.length);
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-gray-900/90 backdrop-blur-sm" onClick={onClose}>
      <div className="flex items-start justify-between gap-3 p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 text-sm">
          <div className="font-medium">{catTR(item.category)} · {index + 1}/{items.length}</div>
          <div className="text-white/60 text-xs mt-0.5 flex flex-wrap gap-x-3">
            {item.createTime && <span>{fmtDate(item.createTime)}</span>}
            {item.width && item.height && <span>{item.width}×{item.height}</span>}
            {item.author && <span>Yükleyen: {item.author}</span>}
            {item.views != null && <span>{nf.format(item.views)} görüntülenme</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a href={item.url} target="_blank" rel="noreferrer" title="Tam boy aç"
             className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white">
            <ExternalLink className="w-4 h-4" />
          </a>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white" title="Kapat (Esc)">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-4 pb-6 gap-2">
        {items.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(-1); }}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        {item.format === 'VIDEO' ? (
          <video src={item.url} controls autoPlay className="max-h-full max-w-full rounded-lg"
                 onClick={(e) => e.stopPropagation()} />
        ) : (
          <img src={item.url} alt={catTR(item.category)} referrerPolicy="no-referrer"
               onClick={(e) => e.stopPropagation()}
               className="max-h-full max-w-full object-contain rounded-lg" />
        )}
        {items.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(1); }}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function GbpLocationModal({ location, brandId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Büyütülen fotoğraf: { items, index }. Escape önce bunu kapatır.
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Büyütme açıkken Escape yalnızca onu kapatsın — pencere ayakta kalır
      setViewer((v) => {
        if (v) return null;
        onClose();
        return v;
      });
    };
    document.addEventListener('keydown', onKey);
    // Arka plan tablosu pencerenin altında kaymasın
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const load = useCallback(async () => {
    if (!location) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/gbp-brand/location/${location.id}`, {
        params: { account: location.accountName },
        headers: { 'X-Brand-ID': brandId },
      });
      setDetail(r.data);
    } catch (e) {
      const d = e?.response?.data;
      setErr(d && typeof d === 'object' ? d : { error: `${e?.response?.status || ''} ${e?.message || ''}`.trim() });
    } finally {
      setLoading(false);
    }
  }, [location, brandId]);

  useEffect(() => { load(); setViewer(null); }, [load]);

  /**
   * Yanıtı Google'a yazar ve yalnızca ilgili yorumu yerinde günceller — tüm
   * detayı yeniden çekmek pencereyi sıfırlar, kullanıcı yazdığı yerde kalmalı.
   * "yanıtsız" sayacı da aynı anda düşürülür.
   */
  const submitReply = useCallback(async (reviewId, comment) => {
    const r = await api.put(
      `/gbp-brand/location/${location.id}/reviews/${reviewId}/reply`,
      { comment, account: location.accountName },
      { headers: { 'X-Brand-ID': brandId } }
    );
    setDetail((prev) => {
      if (!prev) return prev;
      const items = prev.reviews.items.map((x) => (
        x.id === reviewId ? { ...x, reply: r.data.reply, replyTime: r.data.replyTime } : x
      ));
      return { ...prev, reviews: { ...prev.reviews, items, unreplied: items.filter((x) => !x.reply).length } };
    });
  }, [location, brandId]);

  const deleteReply = useCallback(async (reviewId) => {
    await api.delete(`/gbp-brand/location/${location.id}/reviews/${reviewId}/reply`, {
      params: { account: location.accountName },
      headers: { 'X-Brand-ID': brandId },
    });
    setDetail((prev) => {
      if (!prev) return prev;
      const items = prev.reviews.items.map((x) => (
        x.id === reviewId ? { ...x, reply: '', replyTime: null } : x
      ));
      return { ...prev, reviews: { ...prev.reviews, items, unreplied: items.filter((x) => !x.reply).length } };
    });
  }, [location, brandId]);

  if (!location) return null;

  const rv = detail?.reviews;
  const media = detail?.media;
  // Galeri: kapak/logo önce, sonra mekân kareleri; her grup kendi başlığıyla
  const mediaGroups = (() => {
    if (!media?.items?.length) return [];
    const by = new Map();
    for (const m of media.items) {
      if (!by.has(m.category)) by.set(m.category, []);
      by.get(m.category).push(m);
    }
    return [...by.entries()].sort((a, b) => {
      const ia = MEDIA_CATEGORY_ORDER.indexOf(a[0]);
      const ib = MEDIA_CATEGORY_ORDER.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  })();
  const totals = detail?.performance?.totals || {};
  const impressions = (totals.BUSINESS_IMPRESSIONS_DESKTOP_MAPS || 0) + (totals.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH || 0)
    + (totals.BUSINESS_IMPRESSIONS_MOBILE_MAPS || 0) + (totals.BUSINESS_IMPRESSIONS_MOBILE_SEARCH || 0);
  const mobile = (totals.BUSINESS_IMPRESSIONS_MOBILE_MAPS || 0) + (totals.BUSINESS_IMPRESSIONS_MOBILE_SEARCH || 0);
  const maps = (totals.BUSINESS_IMPRESSIONS_DESKTOP_MAPS || 0) + (totals.BUSINESS_IMPRESSIONS_MOBILE_MAPS || 0);
  const range = detail?.performance?.range;

  const fullAddress = [location.addressLine, location.postalCode, location.locality, location.adminArea]
    .filter(Boolean).join(', ');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-[2px]" onClick={onClose} />

      <div className="relative bg-gray-50 rounded-2xl shadow-2xl w-full max-w-5xl my-4 overflow-hidden">
        {/* Başlık */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900">{location.title || 'İsimsiz lokasyon'}</h2>
              {location.status && location.status !== 'OPEN' && (
                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px] border border-amber-200">
                  {location.status === 'CLOSED_PERMANENTLY' ? 'Kalıcı kapalı'
                    : location.status === 'CLOSED_TEMPORARILY' ? 'Geçici kapalı' : location.status}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 mt-0.5 truncate">
              {fullAddress || '—'}
            </div>
            <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1"><Store className="w-3 h-3" />{location.accountLabel}</span>
              <span className="font-mono">{location.id}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {location.mapsUri && (
              <a href={location.mapsUri} target="_blank" rel="noreferrer" title="Google Haritalar'da aç"
                 className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800">
                <MapPin className="w-4 h-4" />
              </a>
            )}
            {location.websiteUri && (
              <a href={location.websiteUri} target="_blank" rel="noreferrer" title="Web sitesi"
                 className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800">
                <Globe className="w-4 h-4" />
              </a>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800" title="Kapat (Esc)">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {err && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{err.error || err.message}</div>
            </div>
          )}
          {detail?.errors?.map((e, i) => (
            <div key={i} className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div><span className="font-medium">{e.where}:</span> {e.hint || e.message}</div>
            </div>
          ))}

          {/* Puan bandı */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-wrap items-center gap-x-8 gap-y-3">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Puan ve yorumlar alınıyor…
              </div>
            ) : rv?.reviewCount ? (
              <>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-semibold text-gray-900 leading-none">{rv.rating?.toFixed(1)}</span>
                    <Stars value={rv.rating} size="w-5 h-5" />
                  </div>
                  <div className="text-xs text-gray-500 mt-1.5">{nf.format(rv.reviewCount)} yorum</div>
                </div>
                <div className="h-10 w-px bg-gray-100 hidden sm:block" />
                <div>
                  <div className="text-sm text-gray-700">
                    Son {rv.items.length} yorumun <span className="font-semibold">{rv.unreplied}</span> tanesi yanıtsız
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1">Tüm geçmiş değil, çekilen son sayfa üzerinden</div>
                </div>
                {location.newReviewUri && (
                  <a href={location.newReviewUri} target="_blank" rel="noreferrer"
                     className="ml-auto text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                    Yorum yazma bağlantısı <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </>
            ) : (
              <div className="text-sm text-gray-500">Bu lokasyonda henüz yorum yok.</div>
            )}
          </div>

          {/* Fotoğraflar */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
              <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-gray-400" /> Fotoğraflar
                {media?.count ? <span className="text-gray-400 font-normal">({nf.format(media.count)})</span> : null}
              </h3>
              {media && !media.hasCover && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  Kapak fotoğrafı tanımlı değil
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Fotoğraflar yükleniyor…
              </div>
            ) : mediaGroups.length ? (
              <div className="space-y-4">
                {mediaGroups.map(([cat, items]) => (
                  <div key={cat}>
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">
                      {catTR(cat)} <span className="text-gray-300">· {items.length}</span>
                    </div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                      {items.map((m) => (
                        <PhotoTile
                          key={m.id}
                          item={m}
                          onOpen={() => setViewer({
                            items: media.items,
                            index: media.items.findIndex((x) => x.id === m.id),
                          })}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-4 text-center">
                Bu lokasyonun Google profilinde hiç fotoğraf yok.
                <div className="text-[11px] text-gray-400 mt-1">
                  Fotoğrafsız profiller aramada belirgin biçimde daha az tıklanır.
                </div>
              </div>
            )}

            {/* Müşteri kareleri — işletme silemez/yönetemez */}
            {!loading && media?.customers?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-gray-100">
                <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">
                  Müşteri fotoğrafları
                  <span className="text-gray-300"> · {nf.format(media.customerTotal || media.customers.length)}</span>
                  {media.customerTotal > media.customers.length && (
                    <span className="text-gray-300"> (en yeni {media.customers.length} kare)</span>
                  )}
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                  {media.customers.map((m) => (
                    <PhotoTile
                      key={m.id}
                      item={m}
                      onOpen={() => setViewer({
                        items: media.customers,
                        index: media.customers.findIndex((x) => x.id === m.id),
                      })}
                    />
                  ))}
                </div>
                <div className="text-[11px] text-gray-400 mt-1.5">
                  Müşterilerin yüklediği kareler işletme tarafından silinemez.
                </div>
              </div>
            )}
          </div>

          {/* Performans */}
          {!loading && detail?.performance?.series?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-baseline gap-2">
                <h3 className="text-sm font-medium text-gray-900">Performans</h3>
                <span className="text-[11px] text-gray-400">
                  {range?.start && range?.end ? `${fmtDate(range.start)} – ${fmtDate(range.end)}` : 'son 30 gün'}
                  {' '}· Google verisi 2-3 gün gecikmeli
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile icon={Eye} label="Gösterim" value={impressions}
                          sub={impressions ? `%${Math.round((mobile / impressions) * 100)} mobil · %${Math.round((maps / impressions) * 100)} Haritalar` : null} />
                <StatTile icon={Navigation} label="Yol tarifi" value={totals.BUSINESS_DIRECTION_REQUESTS} />
                <StatTile icon={Phone} label="Telefon tıklaması" value={totals.CALL_CLICKS} />
                <StatTile icon={MousePointerClick} label="Web sitesi tıklaması" value={totals.WEBSITE_CLICKS} />
              </div>
              <ImpressionsChart series={detail.performance.series} />
            </div>
          )}

          {/* Künye + saatler */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-1">Künye</h3>
              <div className="divide-y divide-gray-50">
                <Field icon={Phone} label="Telefon">
                  {location.phone || null}
                  {location.additionalPhones?.length > 0 && (
                    <div className="text-xs text-gray-500 mt-0.5">{location.additionalPhones.join(' · ')}</div>
                  )}
                </Field>
                <Field icon={Globe} label="Web sitesi">
                  {location.websiteUri
                    ? <a href={location.websiteUri} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{location.websiteUri}</a>
                    : null}
                </Field>
                <Field icon={Tag} label="Kategori">
                  {location.category || null}
                  {location.additionalCategories?.length > 0 && (
                    <div className="text-xs text-gray-500 mt-0.5">{location.additionalCategories.join(' · ')}</div>
                  )}
                </Field>
                <Field icon={MapPin} label="Adres">{fullAddress || null}</Field>
                <Field icon={Hash} label="Mağaza kodu">{location.storeCode || null}</Field>
                <Field icon={Hash} label="Place ID">
                  {location.placeId ? <span className="font-mono text-xs">{location.placeId}</span> : null}
                </Field>
                <Field icon={Tag} label="Etiketler">
                  {location.labels?.length ? location.labels.join(' · ') : null}
                </Field>
                <Field icon={Navigation} label="Hizmet bölgesi">
                  {location.serviceArea?.length ? location.serviceArea.join(' · ') : null}
                </Field>
                <Field icon={MessageSquare} label="Açıklama">{location.description || null}</Field>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" /> Çalışma saatleri
              </h3>
              {location.hours?.length ? (
                <table className="w-full text-sm">
                  <tbody>
                    {location.hours.map((h) => (
                      <tr key={h.day} className="border-b border-gray-50 last:border-0">
                        <td className="py-1.5 text-gray-600 w-32">{DAY_TR[h.day] || h.day}</td>
                        <td className={`py-1.5 text-right ${h.ranges.length ? 'text-gray-900' : 'text-gray-400'}`}>
                          {h.ranges.length ? h.ranges.join(', ') : 'Kapalı'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-sm text-gray-400">Google'da çalışma saati tanımlı değil.</div>
              )}
              {location.specialHours?.length > 0 && (
                <div className="mt-3 text-[11px] text-gray-500">
                  {location.specialHours.length} özel gün tanımlı (tatil/istisna saatleri).
                </div>
              )}
            </div>
          </div>

          {/* Yorumlar */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Son yorumlar {rv?.items?.length ? <span className="text-gray-400 font-normal">({rv.items.length} / {nf.format(rv.reviewCount)})</span> : null}
            </h3>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Yorumlar yükleniyor…
              </div>
            ) : rv?.items?.length ? (
              <div className="space-y-4 max-h-[460px] overflow-y-auto pr-1">
                {rv.items.map((r) => (
                  <ReviewCard key={r.id} review={r} onSubmit={submitReply} onDelete={deleteReply} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-400 py-4 text-center">Gösterilecek yorum yok.</div>
            )}
          </div>
        </div>
      </div>

      {viewer && (
        <PhotoViewer
          items={viewer.items}
          index={viewer.index}
          onIndex={(i) => setViewer((v) => ({ ...v, index: i }))}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
